/**
 * ListDealsCard — branded inline rendering for the `list_deals` tool result.
 *
 * HANDOFF_AI_CHAT_FEATURE_V1_REVISED visual-feedback addendum §4.
 *
 * V1.5 refactor: rows are now `<button>` selection rows, not `<Link>` to
 * the deal page. Clicking loads the deal into chat context (setDealContext +
 * synthetic confirmation message) so the conversation can continue without
 * the user leaving chat. If a user wants to go to the deal page, the model
 * uses navigate_to(destination='deal_detail', deal_id=N) — kept under
 * model control. Same selection flow as ChatDealSelectionCard.
 */
import { useState } from 'react';
import { Check } from 'react-feather';

import { Badge, FinancialNumber, ScoreDisplay } from '../../../components/ui';
import { useChatStore } from '../../../stores/chatStore';
import { useChatStream } from '../hooks/useChatStream';
import type { ToolResultProps } from '../lib/component-registry';

interface ListedDeal {
  id: number;
  deal_name?: string | null;
  bank_name?: string | null;
  financing_purpose?: string | null;
  financing_amount?: number | null;
  currency?: string | null;
  score?: number | null;
  status?: string | null;
  term_sheets_count?: number | null;
}

const PURPOSE_LABELS: Record<string, string> = {
  acquisition: 'Acquisition',
  growth: 'Growth',
  refinancing: 'Refinancing',
  working_capital: 'Working capital',
  capex: 'Capex',
  real_estate: 'Real estate',
  expansion: 'Growth',
  equipment: 'Equipment',
  other: 'Other',
};

export function ListDealsCard({ result }: ToolResultProps) {
  const setDealContext = useChatStore((s) => s.setDealContext);
  const dealContext = useChatStore((s) => s.dealContext);
  const { send } = useChatStream();
  const deals = Array.isArray(result) ? (result as ListedDeal[]) : [];
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const handleSelect = (deal: ListedDeal) => {
    setSelectedId(deal.id);
    setDealContext({
      deal_id: deal.id,
      deal_name: deal.deal_name ?? undefined,
    });
    const name = deal.deal_name ?? `Deal #${deal.id}`;
    // V1.4.1 — synthetic: hidden from the chat thread, sent to the
    // backend so the model picks up the explicit deal_id binding.
    send({
      text:
        `I want to discuss deal: "${name}" (deal_id: ${deal.id}). ` +
        `Please confirm and continue.`,
      synthetic: true,
    });
  };

  if (deals.length === 0) {
    return (
      <div className="mt-2 border border-border bg-surface-subtle px-3 py-3 text-body-sm text-txt-secondary">
        No deals yet. Create one from the dashboard to get started.
      </div>
    );
  }

  // Once a deal is picked, collapse the list to a static confirmation of just
  // that deal — the other deals disappear and this stays in the thread. Local
  // selectedId is authoritative in-session (each card instance keeps its own
  // pick); on reload, fall back to the active dealContext when it belongs to
  // this card's list.
  const activeId = dealContext?.deal_id ?? null;
  const effectiveSelectedId =
    selectedId ?? (activeId !== null && deals.some((d) => d.id === activeId) ? activeId : null);
  const selectedDeal =
    effectiveSelectedId !== null ? (deals.find((d) => d.id === effectiveSelectedId) ?? null) : null;

  if (selectedDeal) {
    return (
      <div className="animate-card-enter mt-2 border border-border bg-surface-card px-3 py-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 shrink-0 text-orange">
            <Check size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-body font-semibold text-txt-primary">
              {selectedDeal.deal_name || `Deal #${selectedDeal.id}`}
            </p>
            {selectedDeal.bank_name && (
              <p className="mt-0.5 truncate text-body-sm text-txt-secondary">
                {selectedDeal.bank_name}
              </p>
            )}
          </div>
          {typeof selectedDeal.score === 'number' && (
            <div className="shrink-0">
              <ScoreDisplay score={Math.round(selectedDeal.score)} size="sm" showLabel={false} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="custom-scrollbar mt-2 flex max-h-[480px] flex-col divide-y divide-border overflow-y-auto border border-border bg-surface-card">
      {deals.map((deal) => (
        <DealRow key={deal.id} deal={deal} onSelect={handleSelect} />
      ))}
    </div>
  );
}

function DealRow({
  deal,
  onSelect,
}: {
  deal: ListedDeal;
  onSelect: (_deal: ListedDeal) => void;
}) {
  const purposeLabel = deal.financing_purpose
    ? (PURPOSE_LABELS[deal.financing_purpose] ?? deal.financing_purpose)
    : null;

  return (
    <button
      type="button"
      onClick={() => onSelect(deal)}
      className="flex w-full items-start gap-3 px-3 py-3 text-left transition-colors hover:bg-surface-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-body font-semibold text-txt-primary">
          {deal.deal_name || `Deal #${deal.id}`}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm text-txt-secondary">
          {deal.bank_name && <span className="truncate">{deal.bank_name}</span>}
          {purposeLabel && <Badge variant="purpose" size="sm">{purposeLabel}</Badge>}
          {deal.status && <Badge variant="neutral" size="sm">{deal.status}</Badge>}
        </div>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-body-sm text-txt-secondary">
          {typeof deal.financing_amount === 'number' && deal.financing_amount > 0 && (
            <span className="font-medium text-txt-primary">
              <FinancialNumber value={deal.financing_amount} format="currency" />
            </span>
          )}
          {typeof deal.term_sheets_count === 'number' && deal.term_sheets_count > 0 && (
            <span>
              {deal.term_sheets_count} term sheet{deal.term_sheets_count === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>
      {typeof deal.score === 'number' && (
        <div className="shrink-0">
          <ScoreDisplay score={Math.round(deal.score)} size="sm" showLabel={false} />
        </div>
      )}
    </button>
  );
}
